/**
 * Định tuyến — bảng ánh xạ theo docs/08 §8.1.
 *
 * Từ M7 **không còn route "Sắp có" nào**: bảy mốc M0–M7 đã bàn giao, và mục
 * cuối cùng (`/dns`) hoá ra không phải việc còn nợ mà là một mục sidebar của
 * FR-001 không có màn tương ứng trong prototype — nay chuyển hướng về
 * `/domains`. Xem OQ-095.
 */

import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from '../lib/auth';

import { AppShell } from './AppShell';
import { ForbiddenState, SkeletonCard } from '../components/States';
import { LoginPage } from '../pages/auth/LoginPage';
import { ForgotPasswordPage, ResetPasswordPage } from '../pages/auth/PasswordPages';
import { InvitePage, VerifyEmailPage } from '../pages/auth/InvitePage';
import { RegisterPage, VerifySignupPage } from '../pages/auth/RegisterPage';
import { OverviewPage } from '../pages/OverviewPage';
import { ProfilePage } from '../pages/ProfilePage';
import { ConnectionsPage } from '../pages/ConnectionsPage';
import { DomainsPage } from '../pages/DomainsPage';
import { ReconcilePage } from '../pages/ReconcilePage';
import { SubdomainsPage } from '../pages/SubdomainsPage';
import { SubdomainDetailPage } from '../pages/SubdomainDetailPage';
import { DomainDetailPage } from '../pages/DomainDetailPage';
import { RolesPage } from '../pages/admin/RolesPage';
import { DomainRolesPage } from '../pages/admin/DomainRolesPage';
import { PermissionsPage } from '../pages/permissions';
import { UsersPage } from '../pages/admin/UsersPage';
import { SettingsPage } from '../pages/admin/SettingsPage';
import { AuditLogPage } from '../pages/audit/AuditLogPage';
import { NotFoundPage } from '../pages/Placeholders';

function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading, mustChangePassword } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="content" style={{ maxWidth: 720, margin: '48px auto' }}>
        <SkeletonCard rows={5} />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;

  /*
   * Quản trị viên vừa đặt mật khẩu hộ (OQ-218): mật khẩu tạm đã đi qua tay
   * người thứ ba, nên nó chỉ được sống tới màn đổi mật khẩu.
   *
   * Chặn ở đây thay vì ở từng trang: mọi đường vào app đều qua `RequireAuth`,
   * kể cả người dùng gõ thẳng URL. Chừa `/profile` để chính màn đổi mật khẩu
   * mở được — nếu chặn cả nó thì vòng chuyển hướng không có lối ra.
   */
  if (mustChangePassword && !location.pathname.startsWith('/profile')) {
    return <Navigate to="/profile?tab=security&doi-mat-khau=1" replace />;
  }

  return <AppShell>{children}</AppShell>;
}

/** Chặn ở FE chỉ là trải nghiệm — backend vẫn là nơi quyết định (docs/10 §RÀNG BUỘC 1). */
function RequireCapability({
  capability,
  deniedOdId,
  children,
}: {
  capability: string;
  deniedOdId?: string;
  children: ReactNode;
}) {
  const { can } = useAuth();
  if (!can(capability)) {
    return (
      <section className="card" {...(deniedOdId ? { 'data-od-id': deniedOdId } : {})}>
        <ForbiddenState />
      </section>
    );
  }
  return <>{children}</>;
}

function GuestOnly({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/** docs/05 §5.6 — mục hiện khi có ÍT NHẤT MỘT trong các capability liệt kê. */
function RequireAnyCapability({
  capabilities,
  children,
}: {
  capabilities: string[];
  children: ReactNode;
}) {
  const { can } = useAuth();
  if (!capabilities.some((cap) => can(cap))) return <ForbiddenState />;
  return <>{children}</>;
}

export function AppRoutes() {
  return (
    <Routes>
      {/* Ngoài shell */}
      <Route
        path="/login"
        element={
          <GuestOnly>
            <LoginPage />
          </GuestOnly>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <GuestOnly>
            <ForgotPasswordPage />
          </GuestOnly>
        }
      />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/dat-lai-mat-khau" element={<ResetPasswordPage />} />
      <Route
        path="/register"
        element={
          <GuestOnly>
            <RegisterPage />
          </GuestOnly>
        }
      />
      <Route
        path="/dang-ky"
        element={
          <GuestOnly>
            <RegisterPage />
          </GuestOnly>
        }
      />
      {/* Không bọc `GuestOnly`: người bấm liên kết trong thư có thể đang đăng
          nhập bằng tài khoản khác, và đá họ về trang chủ sẽ nuốt mất token. */}
      <Route path="/xac-minh" element={<VerifySignupPage />} />
      <Route path="/invite/:token" element={<InvitePage />} />
      <Route path="/kich-hoat/:token" element={<InvitePage />} />
      <Route path="/xac-minh-email" element={<VerifyEmailPage />} />

      {/* Trong shell */}
      <Route
        path="/"
        element={
          <RequireAuth>
            <OverviewPage />
          </RequireAuth>
        }
      />
      <Route
        path="/profile"
        element={
          <RequireAuth>
            <ProfilePage />
          </RequireAuth>
        }
      />
      <Route
        path="/admin/roles"
        element={
          <RequireAuth>
            <RequireCapability capability="adm.roles">
              <RolesPage />
            </RequireCapability>
          </RequireAuth>
        }
      />
      <Route
        path="/admin/domain-roles"
        element={
          <RequireAuth>
            <RequireCapability capability="adm.domainroles">
              <DomainRolesPage />
            </RequireCapability>
          </RequireAuth>
        }
      />
      <Route
        path="/admin/users"
        element={
          <RequireAuth>
            <RequireCapability capability="adm.users" deniedOdId="users-denied">
              <UsersPage />
            </RequireCapability>
          </RequireAuth>
        }
      />
      <Route
        path="/connections"
        element={
          <RequireAuth>
            <RequireAnyCapability capabilities={['conn.own', 'conn.all']}>
              <ConnectionsPage />
            </RequireAnyCapability>
          </RequireAuth>
        }
      />
      <Route
        path="/domains"
        element={
          <RequireAuth>
            <RequireCapability capability="dom.view">
              <DomainsPage />
            </RequireCapability>
          </RequireAuth>
        }
      />
      <Route
        path="/admin/settings"
        element={
          <RequireAuth>
            <RequireCapability capability="adm.settings" deniedOdId="settings-denied">
              <SettingsPage />
            </RequireCapability>
          </RequireAuth>
        }
      />
      {/*
        OQ-283 — PHẢI đứng TRƯỚC `/domains/:id`, nếu không `:id` nuốt luôn
        `reconcile` và người dùng nhận "không tìm thấy tên miền".
      */}
      <Route
        path="/domains/reconcile"
        element={
          <RequireAuth>
            <RequireCapability capability="dom.view">
              <ReconcilePage />
            </RequireCapability>
          </RequireAuth>
        }
      />
      {/*
        OQ-291 — KHÔNG bọc `RequireCapability`.

        Người chỉ được chia sẻ một sub-domain không có `dom.view` (quyền hệ
        thống, gắn với vai trò). Đòi nó ở đây sẽ chặn đúng những người mà trang
        này sinh ra để phục vụ. Phạm vi do máy chủ thu hẹp, như FR-133.
      */}
      <Route
        path="/subdomains"
        element={
          <RequireAuth>
            <SubdomainsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/subdomains/:id"
        element={
          <RequireAuth>
            <SubdomainDetailPage />
          </RequireAuth>
        }
      />
      <Route
        path="/domains/:id"
        element={
          <RequireAuth>
            <RequireCapability capability="dom.view">
              <DomainDetailPage />
            </RequireCapability>
          </RequireAuth>
        }
      />
      {/* FR-133: mở cho mọi người đã đăng nhập, phạm vi do máy chủ thu hẹp. */}
      <Route
        path="/audit-log"
        element={
          <RequireAuth>
            <AuditLogPage />
          </RequireAuth>
        }
      />

      {/* M5 — FR-070…085, tab "Theo người dùng" cần adm.users (docs/05 §5.6) */}
      <Route
        path="/permissions"
        element={
          <RequireAuth>
            <PermissionsPage />
          </RequireAuth>
        }
      />

      {/*
       * FR-001 liệt kê mục sidebar *DNS records*, nhưng `docs/08 §8.1` KHÔNG có
       * route `/dns` và thư mục gốc **không có `dns.html`** — prototype có đúng
       * 11 màn, không màn nào là danh sách bản ghi DNS toàn hệ thống.
       *
       * Nguồn sự thật cho bản ghi DNS đã tồn tại: `/domains/:id?tab=dns`, dựng ở
       * M4 và đúng `docs/08`. Dựng thêm một màn nữa là tạo **cổng thứ hai** cho
       * cùng một thứ mà không tài liệu nào tả nội dung, cột, bộ lọc hay hành
       * động của nó — đúng thứ `docs/11 §11.8.1` cấm ("màn hình cần hiển thị
       * điều gì" ≠ "cần thêm hàng dữ liệu nào"). Xem OQ-095.
       *
       * Mục sidebar đã GỠ ở OQ-118 theo yêu cầu chủ dự án, nhưng route thì
       * GIỮ: gỡ nốt sẽ biến mọi dấu trang `/dns` cũ thành trang 404. Chuyển
       * hướng không tốn gì và không ai nhìn thấy nó nữa trừ khi từng lưu.
       */}
      <Route
        path="/dns"
        element={
          <RequireAuth>
            <Navigate to="/domains" replace />
          </RequireAuth>
        }
      />

      <Route
        path="*"
        element={
          <RequireAuth>
            <NotFoundPage />
          </RequireAuth>
        }
      />
    </Routes>
  );
}
